import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X } from 'lucide-react';

import { feedback as feedbackApi } from '../../lib/api';
import {
  Button,
  Field,
  Stack,
  Textarea,
} from '../ui-kit';
import { StarRating } from './StarRating';

/**
 * Modal prompting the athlete to rate a specific training.
 * Privacy: the server stores athlete_id but never exposes it to the coach.
 */
export const RateTrainingModal = ({ training, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) return;
    setBusy(true);
    try {
      await feedbackApi.submit(training.id, {
        rating,
        comment: comment.trim() || null,
      });
      toast.success(t('feedback.thanks'));
      onSubmitted?.(training.id);
      onClose?.();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        // Already rated this training — close silently to avoid noise.
        onSubmitted?.(training.id);
        onClose?.();
        return;
      }
      if (status === 429) {
        toast.error(t('feedback.tooManyToday'));
      } else {
        toast.error(t('feedback.submitError'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      data-testid="rate-training-modal"
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-text">
            {t('feedback.rateThisTraining')}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label={t('common.cancel')}
            data-testid="rate-training-modal-close"
          >
            <X size={20} />
          </Button>
        </div>
        <form onSubmit={handleSubmit}>
          <Stack gap={4}>
            <Field label={t('feedback.rateYourCoach')} required>
              <StarRating
                value={rating}
                onChange={setRating}
                size={36}
                testId="rate-training-stars"
              />
            </Field>
            <Field label={t('feedback.comment')}>
              <Textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={2000}
                placeholder={t('feedback.commentPlaceholder')}
                data-testid="rate-training-comment"
              />
            </Field>
            <Stack direction="row" gap={3}>
              <Button
                variant="secondary"
                block
                onClick={onClose}
                data-testid="rate-training-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                block
                type="submit"
                disabled={rating < 1 || busy}
                data-testid="rate-training-submit"
              >
                {busy ? '…' : t('feedback.submit')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </div>
    </div>
  );
};

export default RateTrainingModal;
